<template>
  <WdsSelect
    v-model="selectedAgencies"
    multi-select
    searchable
    class="tw-min-w-[150px]"
    data-test="linked-staffing-agencies-filter"
    :label="meta.filter.quickFilter ? '' : $t('Linked staffing agencies')"
    :list="agencyOptions"
    :placeholder="$t('Select staffing agencies to filter by')"
    :max-labels-visible="meta.filter.quickFilter ? 1 : 5"
    can-deselect
    labels-primary
  />
</template>

<script setup lang="ts">
import { computed, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsSelect } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'

type AgencyOption = { label: string; value: string }

const selectedAgencies: ModelRef<AgencyOption[]> = defineModel({
  default: () => [],
})

defineProps<{ meta: FilterMetaDataProp }>()

const AGENCY_OPTIONS = graphql(`
  query LinkedStaffingAgenciesFilterOptions {
    companyRecruiters(first: 100) {
      data {
        id
        recruiter {
          id
          name
        }
      }
    }
  }
`)

const { result } = useQuery(AGENCY_OPTIONS, {}, { clientId: 'publicApi' })

// One relation row per client can repeat an agency; the filter wants each agency once, in order.
const agencyOptions = computed<AgencyOption[]>(() => {
  const byRecruiter = new Map<string, AgencyOption>()

  for (const relation of result.value?.companyRecruiters?.data ?? []) {
    if (!byRecruiter.has(relation.recruiter.id)) {
      byRecruiter.set(relation.recruiter.id, { label: relation.recruiter.name, value: relation.id })
    }
  }

  return [...byRecruiter.values()].sort((a, b) => a.label.localeCompare(b.label))
})
</script>
